import { loadingManagementApi } from 'feature/loading-management/loadingManagementApi';
import { createSingleAbortableQuery } from 'utils/helpers/abortPreviousRequest';
import { createCacheUpdateHandler } from 'utils/helpers/fetch';
import { concat } from 'ramda';
import { createSnack } from 'snackbar/snackbarSlice';
import { buildSelfAuthListViewResult, sanitizeSelfAuthQueryParams } from './selfAuthListViewUtils';

const getUrl = concat('self-authorizations/');
const selfAuthListViewController = { current: null };

const selfAuthorizationsEndpoints = loadingManagementApi.injectEndpoints({
  endpoints: ({ mutation, query }) => ({
    exportSelfAuthorizations: query({
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            createSnack({
              message: 'Export has been successfully downloaded.',
              options: { variant: 'success' },
              title: 'Action Completed',
            })
          );
        } catch (err) {
          const errorMessage =
            err?.error?.data?.detail ||
            err?.error?.data?.title ||
            err?.error?.error ||
            'Due to an error, your self authorizations could not be exported.';
          dispatch(
            createSnack({
              message: errorMessage,
              options: { persist: true, variant: 'error' },
              title: 'Action Not Completed',
            })
          );
        }
      },
      query: params => ({
        params,
        responseHandler: response => response.blob(),
        url: getUrl('export'),
      }),
    }),
    getFilteredProducts: query({
      query: queryParams => ({
        params: queryParams,
        url: getUrl('get-filter-products'),
      }),
    }),
    getSelfAuthorizationsListView: query({
      providesTags: ['SelfAuthorizationList'],
      queryFn: createSingleAbortableQuery(
        selfAuthListViewController,
        async ({ params }, api, extraOptions, baseQuery, signal) => {
          const sanitizedQueryParams = sanitizeSelfAuthQueryParams(params || {});

          const result = await baseQuery(
            {
              method: 'GET',
              params: sanitizedQueryParams,
              signal,
              url: getUrl('get-self-authorizations'),
            },
            api,
            extraOptions
          );

          if (result.error) {
            return { error: result.error };
          }

          return {
            data: buildSelfAuthListViewResult(result.data),
          };
        }
      ),
    }),
    getUniqueLocationsList: query({
      query: queryParams => ({
        params: queryParams,
        url: getUrl('get-locations'),
      }),
    }),
    toggleSelfAuthorizationActivation: mutation({
      async onQueryStarted(args, handlers) {
        const { dispatch, queryFulfilled } = handlers;

        const handleCacheUpdate = createCacheUpdateHandler(
          loadingManagementApi,
          'getSelfAuthorizationsListView',
          (draft, { isActive, params }) => {
            const rowToUpdate = draft.rows?.find(
              auth => auth.productLocationAuthorizationId === params.productLocationAuthorizationId
            );
            if (rowToUpdate) {
              rowToUpdate.activeStatus = !isActive;
            }
          },
          { invalidateOnError: ['SelfAuthorizationList'] }
        );

        handleCacheUpdate(args, handlers);

        try {
          await queryFulfilled;
          dispatch(
            createSnack({
              message: `Authorization has been successfully turned ${
                args.isActive ? 'off' : 'on'
              }.`,
              options: { variant: 'success' },
              title: 'Action Completed',
            })
          );
        } catch (err) {
          const errorMessage =
            err?.error?.data?.detail ||
            `Due to an error, the authorization could not be turned ${
              args.isActive ? 'off' : 'on'
            }.`;
          dispatch(
            createSnack({
              message: errorMessage,
              options: { variant: 'error' },
              title: 'Action Not Completed',
            })
          );
        }
      },
      queryFn: async ({ activationType, isActive, body, params }, api, extraOptions, baseQuery) => {
        const url = isActive
          ? 'self-authorizations/deactivate-self-authorization'
          : `self-authorizations/activate-${activationType}-authorization`;

        const requestBody = isActive
          ? { productLocationAuthorizationId: params.productLocationAuthorizationId }
          : body;

        const result = await baseQuery(
          {
            body: requestBody,
            method: 'POST',
            url,
          },
          api,
          extraOptions
        );

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data };
      },
    }),
    updateSelfAuthorizationExtendedLoading: mutation({
      invalidatesTags: ['SelfAuthorizationList'],
      onQueryStarted: createCacheUpdateHandler(
        loadingManagementApi,
        'getSelfAuthorizationsListView',
        (draft, { extendedLoadingActivated, productLocationAuthorizationId }) => {
          const rowToUpdate = draft.rows?.find(
            row => row.productLocationAuthorizationId === productLocationAuthorizationId
          );

          if (rowToUpdate) {
            rowToUpdate.extendedLoadingActivated = !extendedLoadingActivated;
          }
        }
      ),
      query: ({ extendedLoadingActivated, productLocationAuthorizationId }) => ({
        body: { productLocationAuthorizationId },
        method: 'POST',
        url: `self-authorizations/${
          extendedLoadingActivated ? 'deactivate' : 'activate'
        }-extended-loading`,
      }),
    }),
  }),
});

export const { useGetUniqueLocationsListQuery, useGetFilteredProductsQuery } =
  selfAuthorizationsEndpoints;

export default selfAuthorizationsEndpoints;
